import React, { useCallback, useEffect, useState } from "react";
import { message, Spin } from "antd";
import ProjectService, {
  ProjectCreateDto,
  EntityDropdownDto,
  CalcProfileDto,
} from "../../services/ProjectService";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import styles from "./CreateProjectModal.module.css";

const projectService = new ProjectService();

interface CreateProjectModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated: (projectId: number) => void;
}

function parseTaxYearNumber(taxYear: string): number {
  const stripped = taxYear.replace(/^FY/i, '');
  const parsed = parseInt(stripped, 10);
  return isNaN(parsed) ? new Date().getFullYear() : parsed;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  visible,
  onClose,
  onCreated,
}) => {
  const { selectedPartnership, taxYear } = usePartnershipContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);
  const [entities, setEntities] = useState<EntityDropdownDto[]>([]);
  const [calcProfile, setCalcProfile] = useState<CalcProfileDto | null>(null);
  const [form, setForm] = useState<ProjectCreateDto & { projectType: string }>({
    name: "",
    entityId: 0,
    taxYear: new Date().getFullYear(),
    engagementType: "SubK",
    projectType: "Period",
  });

  // Auto-fill name and taxYear from context when modal opens
  useEffect(() => {
    if (visible) {
      const yearNum = parseTaxYearNumber(taxYear);
      const autoName = selectedPartnership
        ? `${selectedPartnership.name} — FY${yearNum} Tax Compliance`
        : "";
      const autoEntityId = selectedPartnership ? parseInt(selectedPartnership.id) || 0 : 0;
      setForm((prev) => ({
        ...prev,
        name: autoName,
        taxYear: yearNum,
        entityId: autoEntityId,
      }));
    }
  }, [visible, selectedPartnership, taxYear]);

  const fetchDropdowns = useCallback(async () => {
    setIsLoadingDropdowns(true);
    try {
      const [ents, profile] = await Promise.allSettled([
        projectService.getEntities(),
        selectedPartnership
          ? projectService.getCalcProfile(
              parseInt(selectedPartnership.id) || 0,
              taxYear,
            )
          : Promise.resolve(null),
      ]);
      if (ents.status === 'fulfilled') setEntities(ents.value);
      else message.error("Failed to load entity options");
      if (profile.status === 'fulfilled') setCalcProfile(profile.value);
    } finally {
      setIsLoadingDropdowns(false);
    }
  }, [selectedPartnership, taxYear]);

  useEffect(() => {
    if (visible) {
      fetchDropdowns();
    }
  }, [visible, fetchDropdowns]);

  const handleChange = (
    field: keyof (ProjectCreateDto & { projectType: string }),
    value: string | number,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      message.warning("Project Name is required");
      return;
    }
    if (!form.entityId) {
      message.warning("Entity is required");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: ProjectCreateDto = {
        name: form.name,
        entityId: form.entityId,
        taxYear: form.taxYear,
        engagementType: form.projectType === "Event" ? "Event" : "SubK",
        allocationMethod: "TargetAllocation",
        calcProfileId: calcProfile?.id || undefined,
      };
      const projectId = await projectService.createProject(payload);
      message.success("Project created");
      resetForm();
      onCreated(projectId);
    } catch {
      message.error("Failed to create project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      entityId: 0,
      taxYear: new Date().getFullYear(),
      engagementType: "SubK",
      projectType: "Period",
    });
  };

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  if (!visible) return null;

  return (
    <>
      <div className={styles.overlay} onClick={resetAndClose} />
      <div className={styles.modal}>
        <div className={styles.modalTitle}>Create Project</div>

        {isLoadingDropdowns ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : (
          <div className={styles.formGrid}>
            <div className={styles.formGroup} style={{ gridColumn: "1 / -1" }}>
              <label>Project Name</label>
              <input
                className={styles.formInput}
                placeholder="e.g. Nexus Capital Partners — FY2025 Tax Compliance"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Entity</label>
              <select
                className={styles.formInput}
                value={form.entityId || ""}
                onChange={(e) => handleChange("entityId", parseInt(e.target.value) || 0)}
              >
                <option value="">Select Entity</option>
                {entities.map((ent) => (
                  <option key={ent.id} value={ent.id}>{ent.name}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Tax Year</label>
              <input
                className={styles.formInput}
                type="number"
                value={form.taxYear || ""}
                onChange={(e) => handleChange("taxYear", parseInt(e.target.value) || 0)}
              />
            </div>

            <div className={styles.formGroup} style={{ gridColumn: "1 / -1" }}>
              <label>Type</label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                {(["Period", "Event"] as const).map((type) => (
                  <label
                    key={type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      color: "#3a4a5e",
                      fontWeight: form.projectType === type ? 600 : 400,
                    }}
                  >
                    <input
                      type="radio"
                      name="projectType"
                      value={type}
                      checked={form.projectType === type}
                      onChange={() => handleChange("projectType", type)}
                      style={{ accentColor: "#F26522" }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={styles.modalActions}>
          <button className={styles.btnCancel} onClick={resetAndClose}>
            Cancel
          </button>
          <button
            className={styles.btnSubmit}
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingDropdowns}
          >
            {isSubmitting ? "Creating..." : "Create Project"}
          </button>
        </div>
      </div>
    </>
  );
};
